/**
 * Slices 57-58 — photo groups and the Walk shot list.
 * Each unit item can sit in a photo group inside its room. The cleaner's Walk step is one prompt per
 * group, in the owner's order, plus one whole-room shot (always first) that covers every item with no group.
 * Item names are never shown as running text in this feature: the cleaner sees counts only.
 */
import { normalizeRoomName } from "@/lib/roomUtils";

export interface PhotoGroup {
  id: string;
  property_id: string;
  room_id: string;
  name: string;
  prompt: string | null;
  reference_photo_url: string | null;
  sort_order: number;
}

export const WHOLE_ROOM_KEY = "room";

export type RoomShot = {
  /** "room" for the whole-room shot, otherwise the photo group id. */
  key: string;
  kind: "room" | "group";
  /** Short label for lists (Whole kitchen, Cutlery drawer). */
  title: string;
  /** What the cleaner is told to do. */
  prompt: string;
  /** Picture of what should be there; null when the owner hasn't added one. */
  refUrl: string | null;
  /** Items this shot is about, as a count only. */
  itemCount: number;
  /** The names of those items (unit items, supplies and appliances). */
  itemNames: string[];
};

/** "Photo the …" / "Open the …" wording, same rule the owner screen prefills with. */
export function defaultGroupPrompt(name: string): string {
  const n = name.trim().toLowerCase();
  if (/^under/.test(n)) return `Open the cupboard ${n}`;
  if (/drawer|cupboard|wardrobe|sideboard|cabinet|closet/.test(n)) return `Open the ${n}`;
  return `Photo the ${n}`;
}

export function groupPrompt(group: Pick<PhotoGroup, "name" | "prompt">): string {
  return group.prompt?.trim() || defaultGroupPrompt(group.name);
}

/** The label an owner reads: "4 sofa cushions" when a quantity is set, else the plain name. */
export function itemLabel(name: string, quantity?: number | null): string {
  return quantity && quantity > 0 ? `${quantity} ${name.toLowerCase()}` : name;
}

export function groupsForRoom(groups: PhotoGroup[], roomId: string): PhotoGroup[] {
  return groups
    .filter((g) => g.room_id === roomId)
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}

/** The room's ordered shot list: whole room first, then one per photo group. */
export function buildRoomShots(input: {
  roomName: string;
  coverUrl: string | null;
  groups: PhotoGroup[];
  items: { id: string; name?: string | null; photo_group_id?: string | null }[];
}): RoomShot[] {
  const groupIds = new Set(input.groups.map((g) => g.id));
  const looseItems = input.items.filter((i) => !i.photo_group_id || !groupIds.has(i.photo_group_id));
  const loose = looseItems.length;
  const shots: RoomShot[] = [
    {
      key: WHOLE_ROOM_KEY,
      kind: "room",
      title: `Whole ${input.roomName.toLowerCase()}`,
      prompt: `Photo the whole ${input.roomName.toLowerCase()}`,
      refUrl: input.coverUrl,
      itemCount: loose,
      itemNames: looseItems.map((i) => i.name || "").filter(Boolean),
    },
  ];
  for (const g of input.groups) {
    shots.push({
      key: g.id,
      kind: "group",
      title: g.name,
      prompt: groupPrompt(g),
      refUrl: g.reference_photo_url,
      itemCount: input.items.filter((i) => i.photo_group_id === g.id).length,
      itemNames: input.items.filter((i) => i.photo_group_id === g.id).map((i) => i.name || "").filter(Boolean),
    });
  }
  return shots;
}

export function sameRoom(a: string | null | undefined, b: string | null | undefined): boolean {
  return normalizeRoomName(a || "") === normalizeRoomName(b || "");
}

export function itemsCountText(n: number): string {
  return `${n} item${n === 1 ? "" : "s"}`;
}
